/** Attachments panel: list, upload (drag & drop / picker / camera), open, archive. */
import { Camera, FileText, Image as ImageIcon, Paperclip, Upload } from "lucide-react";
import { type ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";

import { useAuth } from "@/app/auth";
import { UploadQueue } from "@/features/documents/StagedAttachments";
import {
  type Document,
  type LinkableEntity,
  openDocument,
  useUnlinkDocument,
  useEntityDocuments,
  useDocuments,
  useLinkDocument,
  useUploader,
} from "@/features/documents/queries";
import { EmptyState, ErrorState, LoadingState } from "@/shared/components/page";
import { cn, formatDateTime } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

/** Human-readable size. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Props for `Attachments`. */
export interface AttachmentsProps {
  entityType: LinkableEntity;
  entityId: string;
  /** Show a camera capture button (mobile). */
  camera?: boolean;
  /** Relation label; evidence/checklist/SOP links pin their exact current version. */
  relation?: string;
  /** Compact list without the drop zone (e.g. inside a checklist row). */
  compact?: boolean;
  /** Called after each successful upload (e.g. to link a photo to a checklist answer). */
  onUploaded?: (documentId: string) => void;
  /** Select evidence after upload or library selection, including an attached file. */
  onSelected?: (documentId: string) => void;
  /** Include files already attached to this record in the evidence picker. */
  allowAlreadyLinked?: boolean;
  /** Optional MIME filter for evidence-specific intake. */
  accept?: string;
  /** Restrict library selection and new uploads to a document kind. */
  fileKind?: "photo" | "document";
  /** Expose active transfer or linking state to the owning form's submission guard. */
  onBusyChange?: (busy: boolean) => void;
}

/** Attachment list + uploader for any linkable entity. */
export function Attachments({
  entityType,
  entityId,
  camera = true,
  relation = "attachment",
  compact = false,
  onUploaded,
  onSelected,
  allowAlreadyLinked = false,
  accept,
  fileKind,
  onBusyChange,
}: AttachmentsProps): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const docs = useEntityDocuments(entityType, entityId, can("documents.document.view"));
  const unlink = useUnlinkDocument();
  const linkExisting = useLinkDocument();
  const [showExisting, setShowExisting] = useState(false);
  const [existingSearch, setExistingSearch] = useState("");
  const existing = useDocuments(
    { q: existingSearch || undefined, kind: fileKind },
    showExisting && can("documents.document.upload"),
  );
  const uploader = useUploader({
    kind: fileKind,
    links: [{ entity_type: entityType, entity_id: entityId, relation }],
    onUploaded: (id) => {
      onUploaded?.(id);
      onSelected?.(id);
    },
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const canUpload = can("documents.document.upload");
  const canManage = can("documents.document.manage");
  useEffect(() => {
    onBusyChange?.(uploader.busy || linkExisting.isPending);
  }, [onBusyChange, uploader.busy, linkExisting.isPending]);

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragging(false);
    if (canUpload && e.dataTransfer.files.length) {
      void uploader.upload(e.dataTransfer.files);
    }
  };

  const icon = (d: Document): ReactElement =>
    d.kind === "photo" ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />;

  return (
    <div className="space-y-2">
      {canUpload ? (
        <div
          className={cn(
            "flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2 text-sm",
            dragging && "border-primary bg-primary/5",
            compact && "border-0 p-0",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="dropzone"
        >
          {!compact ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Paperclip className="h-4 w-4" /> {t("documents.dropHint")}
            </span>
          ) : null}
          {canUpload ? (
            <div className="space-y-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowExisting((shown) => !shown)}
              >
                {t("documentLibrary.linkExisting")}
              </Button>
              {showExisting ? (
                <div className="space-y-2 rounded-md border p-2">
                  <Input
                    aria-label={t("documentLibrary.search")}
                    placeholder={t("documentLibrary.search")}
                    value={existingSearch}
                    onChange={(event) => setExistingSearch(event.target.value)}
                  />
                  <ul className="divide-y">
                    {existing.items
                      .filter(
                        (document) =>
                          allowAlreadyLinked ||
                          !docs.data?.some((linked) => linked.id === document.id),
                      )
                      .map((document) => (
                        <li
                          key={document.id}
                          className="flex flex-wrap items-center justify-between gap-2 py-2"
                        >
                          <Link
                            to="/documents/$documentId"
                            params={{ documentId: document.id }}
                            className="min-w-0 flex-1 hover:underline"
                          >
                            {document.code} · {document.title}
                          </Link>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={
                              linkExisting.isPending ||
                              (Boolean(onSelected) && !document.current_version)
                            }
                            onClick={() => {
                              if (
                                allowAlreadyLinked &&
                                docs.data?.some((linked) => linked.id === document.id)
                              ) {
                                onSelected?.(document.id);
                                setShowExisting(false);
                                return;
                              }
                              linkExisting.mutate(
                                {
                                  id: document.id,
                                  entity_type: entityType,
                                  entity_id: entityId,
                                  relation,
                                },
                                {
                                  onSuccess: () => {
                                    onUploaded?.(document.id);
                                    onSelected?.(document.id);
                                    setShowExisting(false);
                                  },
                                },
                              );
                            }}
                          >
                            {t("documentLibrary.link")}
                          </Button>
                        </li>
                      ))}
                  </ul>
                  {existing.query.isPending ? <LoadingState /> : null}
                  {existing.hasMore ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={existing.isLoadingMore}
                      onClick={existing.loadMore}
                    >
                      {t("documentLibrary.loadMore")}
                    </Button>
                  ) : null}
                  {existing.query.isError ? (
                    <p role="alert">{existing.query.error.message}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <input
            ref={fileInput}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            aria-label={t("documents.chooseFiles")}
            onChange={(e) => {
              if (e.target.files) void uploader.upload(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            aria-label={t("documents.takePhoto")}
            onChange={(e) => {
              if (e.target.files) void uploader.upload(e.target.files);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={uploader.busy}
          >
            <Upload className="h-3 w-3" /> {t("documents.upload")}
          </Button>
          {camera ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => cameraInput.current?.click()}
              disabled={uploader.busy}
            >
              <Camera className="h-3 w-3" /> {t("documents.photo")}
            </Button>
          ) : null}
        </div>
      ) : null}
      <UploadQueue uploader={uploader} />
      {actionError ? (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      ) : null}
      {unlink.error || linkExisting.error ? (
        <p role="alert" className="text-sm text-destructive">
          {(unlink.error ?? linkExisting.error)?.message}
        </p>
      ) : null}
      {docs.isPending && docs.fetchStatus !== "idle" ? (
        <LoadingState className="p-2" />
      ) : docs.isError ? (
        <ErrorState error={docs.error} onRetry={() => void docs.refetch()} />
      ) : !docs.data || docs.data.length === 0 ? (
        compact ? null : (
          <EmptyState message={t("documents.empty")} />
        )
      ) : (
        <ul className="divide-y rounded-md border">
          {docs.data.map((d) =>
            (() => {
              const linked = d.links.find(
                (link) => link.entity_type === entityType && link.entity_id === entityId,
              );
              const file = d.effective_version ?? d.current_version;
              return (
                <li key={d.id} className="flex flex-wrap items-center gap-2 p-2 text-sm">
                  {icon(d)}
                  <Link
                    to="/documents/$documentId"
                    params={{ documentId: d.id }}
                    search={{ version_id: linked?.version_id ?? undefined }}
                    className="min-w-0 basis-[calc(100%-2rem)] break-words text-start hover:underline sm:flex-1 sm:basis-auto sm:truncate"
                    title={d.title}
                  >
                    {d.title}
                  </Link>
                  {file ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setActionError(undefined);
                        void openDocument(d.id, linked?.version_id ?? undefined).catch(
                          (error: unknown) =>
                            setActionError(
                              `${t("documents.downloadFailed")} ${error instanceof Error ? error.message : String(error)}`,
                            ),
                        );
                      }}
                    >
                      {t("documentLibrary.download")}
                    </Button>
                  ) : (
                    <Badge tone="warning">{t(`documentLibrary.statusLabels.${d.status}`)}</Badge>
                  )}
                  <Badge tone="outline">
                    {t(`documents.kinds.${d.kind}`, { defaultValue: d.kind })}
                  </Badge>
                  {file ? (
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(file.size_bytes)} · v{file.version_no}
                    </span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(d.updated_at)}
                  </span>
                  {canManage && !compact && linked ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (globalThis.confirm(t("documentLibrary.confirmUnlink"))) {
                          unlink.mutate({ id: d.id, linkId: linked.id });
                        }
                      }}
                      disabled={unlink.isPending}
                    >
                      {t("documentLibrary.unlink")}
                    </Button>
                  ) : null}
                </li>
              );
            })(),
          )}
        </ul>
      )}
    </div>
  );
}
